/**
 * FTPSettings — org-level FTP/SFTP configuration.
 *
 * Shown in the Admin → Organization tab for every org type. An org can
 * configure two independent FTP paths:
 *
 *   - Delivery FTP (outbound): a single external FTP that EVERY delivered
 *     asset is automatically pushed to, on every delivery. Handled entirely
 *     server-side by the onDeliveryCreatedSendToFtp trigger — no cross-org
 *     approval, no rate limit.
 *
 *   - Download FTP (inbound): the org's own FTP. When a campaign is delivered
 *     TO this org, members can push the spots to this FTP from the Download
 *     Manager instead of downloading a ZIP.
 *
 * Each path is a <FtpConfigPanel/>. This wrapper adds the shared audit log,
 * which lists both delivery pushes (sourceType 'delivery') and download-manager
 * pushes (sourceType 'self').
 */

import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from '../Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import FtpConfigPanel from './FtpConfigPanel';

interface FtpAuditEntry {
  id: string;
  sourceType?: 'self' | 'delivery';
  studioOrgName?: string;
  broadcasterOrgName?: string;
  filePath: string;
  remoteName: string;
  status: 'success' | 'failed';
  error?: string;
  timestamp: number;
  triggeredByName?: string;
}

const FTPSettings: React.FC = () => {
  const firebase: any = useContext(FirebaseContext);
  const { currentOrgId } = useOrganizationStore();

  const [auditLog, setAuditLog] = useState<FtpAuditEntry[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load recent audit activity (both delivery + download pushes share the
  // /organizations/{orgId}/ftpAuditLog collection).
  useEffect(() => {
    if (!firebase || !currentOrgId) return;
    (async () => {
      try {
        const q = query(
          collection(firebase.db, `organizations/${currentOrgId}/ftpAuditLog`),
          orderBy('timestamp', 'desc'),
          limit(20)
        );
        const snap = await getDocs(q);
        const list: FtpAuditEntry[] = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        setAuditLog(list);
      } catch (err) {
        console.error('[FTPSettings] failed to load audit log:', err);
      }
    })();
  }, [firebase, currentOrgId, refreshKey]);

  const statusBadge = (s: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      success: { bg: 'rgba(80, 200, 120, 0.2)', color: '#50c878' },
      failed: { bg: 'rgba(255, 75, 75, 0.2)', color: 'var(--button-danger)' },
    };
    const style = map[s] || { bg: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255,255,255,0.5)' };
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600,
        borderRadius: '4px', backgroundColor: style.bg, color: style.color, textTransform: 'capitalize',
      }}>
        {s}
      </span>
    );
  };

  const sourceBadge = (t?: string) => {
    const isDelivery = t === 'delivery';
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', fontSize: '0.65rem', fontWeight: 600,
        borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.03em',
        backgroundColor: isDelivery ? 'rgba(90, 170, 255, 0.18)' : 'rgba(160, 130, 255, 0.18)',
        color: isDelivery ? '#5aaaff' : '#a082ff',
      }}>
        {isDelivery ? 'Delivery' : 'Download'}
      </span>
    );
  };

  return (
    <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <h5 className="mb-2" style={{ color: '#fff' }}>FTP / SFTP Integration</h5>
      <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
        Configure FTP for outgoing deliveries and for receiving spots into your own system.
      </p>

      <FtpConfigPanel
        configType="delivery"
        title="Delivery FTP (outbound)"
        description="Assets you deliver are automatically sent to this external FTP — on every delivery. Restrict it to specific clients below, or leave all unchecked to send every client's assets."
        showClientFilter
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      <FtpConfigPanel
        configType="download"
        title="Download FTP (inbound)"
        description="When a campaign is delivered to you, members can push the spots straight to this FTP from the Download Manager instead of downloading a ZIP."
        showPresetField
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      {/* Shared audit log — delivery pushes + download-manager pushes */}
      {auditLog.length > 0 && (
        <div className="mt-4">
          <h6 className="mb-2" style={{ color: '#fff' }}>Recent Activity (last {auditLog.length})</h6>
          <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {auditLog.map(entry => (
              <div key={entry.id} style={{
                padding: '6px 10px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)',
                backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 4,
                display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
              }}>
                {sourceBadge(entry.sourceType)}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.remoteName}
                  {entry.broadcasterOrgName ? ` → ${entry.broadcasterOrgName}` : (entry.studioOrgName ? ` — ${entry.studioOrgName}` : '')}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                  {new Date(entry.timestamp).toLocaleString()}
                </span>
                {statusBadge(entry.status)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FTPSettings;
